import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import { Box, Button, useTheme } from "@mui/material";
import CustomTypography from "../../components/common/form/CustomTypography";
import { useSearchParams } from "react-router-dom";

import AppButton from "../../components/common/buttons/AppButton";
import { useNotification } from "../../../utils/NotificationsContext";
import LoaderOverlay from "../../components/common/pages/AppLoader";
import AppSearchDropDown2 from "../../components/common/filter/AppSearchDropdown2";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { FilterValue } from "../../../models/Filters/FilterValue";
import TypeFilterCard from "../../components/filters/TypeFilterCard";
import {
  parseQueryParamsForProposalAnalyticsFilters,
  ProposalAnalyticsFilters,
  ProposalAnalyticsSelectedFilters,
} from "../../../models/Dashboard/ProposalDashboard/ProposalAnalyticsFilters";
import { Dayjs } from "dayjs";
import { DateRangePickerNew } from "../../components/common/textFields/DatePickerRangeNew";

const PROPOSAL_ANALYTICS_FILTERS_KEY = "PROPOSAL_ANALYTICS_FILTERS_KEY";

const ProposalDashboard: React.FC = () => {
  const theme = useTheme();

  const { vendorsAnalyticsDashboardStore, orderStore, userStore } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFiltersData, setSelectedFiltersData] = useState<FilterValue[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const { showError } = useNotification();
  let apiTimer: any;

  const initialFilters: ProposalAnalyticsFilters =
    parseQueryParamsForProposalAnalyticsFilters(
      localStorage.getItem(PROPOSAL_ANALYTICS_FILTERS_KEY)
        ? JSON.parse(localStorage.getItem(PROPOSAL_ANALYTICS_FILTERS_KEY)!)
        : null,
      searchParams
    );

  const [filters, setFilters] =
    useState<ProposalAnalyticsFilters>(initialFilters);
  const [selectedFilters, setSelectedFilters] =
    useState<ProposalAnalyticsSelectedFilters>({
      ...initialFilters,
      start_date: initialFilters.start_date.format("YYYY-MM-DD"),
      end_date: initialFilters.end_date.format("YYYY-MM-DD"),
    });

  useEffect(() => {
    updateSelectedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            newSearchParams.set(key, value.map((item) => item.id).join(","));
          }
        } else {
          newSearchParams.set(key, value);
        }
      }
    });

    setSearchParams(newSearchParams);
  }, [selectedFilters]);

  const updateSelectedFilters = () => {
    localStorage.setItem(
      PROPOSAL_ANALYTICS_FILTERS_KEY,
      JSON.stringify(filters)
    );

    const start_date = filters.start_date.format("YYYY-MM-DD");
    const end_date = filters.end_date.format("YYYY-MM-DD");

    setSelectedFilters((_) => ({
      ...filters,
      start_date: start_date,
      end_date: end_date,
    }));

    const newFilters: React.SetStateAction<FilterValue[]> = [];

    if (filters.order_id) {
      filters.order_id.forEach((order_id) =>
        newFilters.push({ id: "order_id", value: order_id.value })
      );
    }

    setSelectedFiltersData(newFilters);
  };

  const handleApply = async () => {
    setLoading(true);

    try {
      setFilters((prev) => ({
        ...prev,
      }));

      await vendorsAnalyticsDashboardStore.getProposalAnalyticsData({
        order_id: selectedFilters.order_id,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
      });
    } catch (error) {
      showError("Error", "Failed to fetch the analytics dashboard details 2.");
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    if (range.startDate && range.endDate) {
      setFilters((prev) => ({
        ...prev,
        start_date: range.startDate!,
        end_date: range.endDate!,
      }));
    }
  };

  const handleOrderIdSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 0)
      apiTimer = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch((err) => {});
      }, 1500);
  };

  const handleRemoveFilter = (index: number) => {
    const filterToRemove = selectedFiltersData[index];
    const updatedFilters = selectedFiltersData.filter((_, i) => i !== index);
    setSelectedFiltersData(updatedFilters);

    switch (filterToRemove.id) {
      case "order_id":
        setFilters((prev) => ({
          ...prev,
          order_id:
            prev.order_id?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;
        break;
      default:
        break;
    }
  };

  const handleClearAllFilters = () => {
    setFilters((_) => ({
      ...initialFilters,
      vendor_id: [],
      product_id: [],
    }));

    setSelectedFiltersData([]);
  };

  return (
    <>
      {userStore.hasPermission(EmployeeRoles.VIEW_PROPOSAL_DASHBOARD) ? (
        <>
          <LoaderOverlay loading={loading} />
          <Box
            sx={{
              margin: { xs: 2, sm: 3, md: 4 },
              padding: { xs: 1, sm: 2, md: 4 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: theme.palette.customColors.white,
                padding: { xs: 2, sm: 3, md: 4 },
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                  gap: { xs: 2, sm: 2, md: 2 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: { xs: "flex-start", sm: "space-around" },
                    alignItems: { xs: "stretch", sm: "center" },
                    flexWrap: "wrap",
                    gap: { xs: 2, sm: 2 },
                    width: { xs: "100%", md: "auto" },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "100%", sm: "200px" },
                      minWidth: { xs: "auto", sm: "180px" },
                    }}
                  >
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.tableGrayHeadingColor}
                      style={{ marginBottom: 10 }}
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.8rem" },
                      }}
                    >
                      Date Range
                    </CustomTypography>
                    <DateRangePickerNew
                      onRangeSelect={handleRangeSelect}
                      startDate={filters.start_date}
                      endDate={filters.end_date}
                    />
                  </Box>

                  <Box
                    sx={{
                      width: { xs: "100%", sm: "200px" },
                      minWidth: { xs: "auto", sm: "180px" },
                    }}
                  >
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.tableGrayHeadingColor}
                      style={{ marginBottom: 10 }}
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.8rem" },
                      }}
                    >
                      {"Order Id"}
                    </CustomTypography>
                    <AppSearchDropDown2
                      showSelection={false}
                      isMultiSelect={true}
                      title="Order Id"
                      value={filters.order_id || []}
                      fontSize="12px"
                      list={orderStore.orderIds.map((item) => {
                        return { id: String(item), value: String(item) };
                      })}
                      local={false}
                      handleSearch={handleOrderIdSearch}
                      handleSelect={(items) => {
                        setFilters((prev) => ({
                          ...prev,
                          order_id: items,
                        }));
                      }}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: 1, sm: 2 },
                    justifyContent: { xs: "center", md: "flex-end" },
                    marginTop: { xs: 1, md: 0 },
                  }}
                >
                  <Box
                    sx={{
                      marginTop: { xs: 0, sm: 3.1 },
                      width: { xs: "50%", sm: "auto" },
                    }}
                  >
                    <AppButton
                      variant="primary"
                      label="Apply"
                      onClick={handleApply}
                      disabled={loading}
                    />
                  </Box>

                  <Box
                    sx={{
                      marginTop: { xs: 0, sm: 3.1 },
                      width: { xs: "50%", sm: "auto" },
                    }}
                  >
                    <AppButton
                      variant="secondary"
                      label="Clear"
                      customStyles={{ width: "100%" }}
                      onClick={() => handleClearAllFilters()}
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ paddingTop: "15px" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: { xs: 1, sm: 2 },
                    borderRadius: 2,
                    paddingLeft: { xs: "10px", sm: "20px", md: "40px" },
                    paddingRight: { xs: "10px", sm: "20px", md: "40px" },
                  }}
                >
                  {selectedFiltersData.map((filter, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor:
                          theme.palette.customColors.lightGreyColor,
                        borderRadius: 5,
                        border: "1px solid #E2E2E2",
                        height: { xs: "28px", sm: "30px" },
                        paddingRight: "5px",
                        minWidth: { xs: "120px", sm: "auto" },
                      }}
                    >
                      <CustomTypography
                        variant="heading8"
                        color={theme.palette.customColors.blackBorder}
                        style={{
                          paddingLeft: "10px",
                          paddingRight: "5px",
                        }}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: { xs: "100px", sm: "150px", md: "200px" },
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        }}
                      >
                        {filter.value}
                      </CustomTypography>
                      <Button
                        onClick={() => handleRemoveFilter(index)}
                        sx={{
                          width: { xs: "12px", sm: "14px" },
                          height: { xs: "12px", sm: "14px" },
                          minWidth: { xs: "12px", sm: "14px" },
                          minHeight: { xs: "12px", sm: "14px" },
                          fontSize: { xs: "12px", sm: "15px" },
                          padding: "0px",
                          border: "1px solid #E2E2E2",
                          borderRadius: "50%",
                          backgroundColor: theme.palette.customColors.white,
                          cursor: "pointer",
                          overflow: "hidden",
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors.lightGreyColor,
                          },
                        }}
                      >
                        ×
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
            <Box sx={{ marginBottom: 3 }}>
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.blackBorder}
                style={{ margin: "12px 10px 0px 0px" }}
              >
                <span style={{ fontStyle: "italic" }}>Note:</span> The analytics
                data is cached. Recent changes may take up to 30 minutes to
                reflect.
              </CustomTypography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                mt: 3,
                gap: { xs: 1, sm: 2 },
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              {vendorsAnalyticsDashboardStore.proposal_tats.map(
                (item, index) => (
                  <Box
                    sx={{
                      flex: {
                        xs: "1 1 140px",
                        sm: "1 1 180px",
                        md: "1 1 200px",
                      },
                      maxWidth: {
                        xs: "calc(50% - 8px)",
                        sm: "calc(33.33% - 16px)",
                        md: "calc(25% - 16px)",
                        lg: "calc(20% - 16px)",
                        xl: "calc(16.66% - 16px)",
                      },
                      minWidth: { xs: "140px", sm: "160px", md: "180px" },
                    }}
                  >
                    <TypeFilterCard
                      title={item.label}
                      message={item.info}
                      value={item.value ?? "0"}
                      icon={
                        "https://content.swageazy.com/icons/boxes_green.png"
                      }
                      onClick={() => {
                        if (item.label === "Unique Visitors") {
                          const dipatch_url = `/report/data?title=${encodeURI(
                            "Proposal data"
                          )}&base_url=/v5/ops-analytics/vendors/scorecard&path=/data/proposal-data&${searchParams.toString()}`;
                          window.open(dipatch_url, "_blank");
                        }
                      }}
                      color={""}
                    />
                  </Box>
                )
              )}
            </Box>
          </Box>
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </>
  );
};

export default observer(ProposalDashboard);
